import React, { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GetCookie from "../../../../utils/GetCookie";
import authNavigate from "../../../../globalComponents/AuthNav";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import SuccessPopup from "../../../../utils/popup/SuccessPopup";
import ProcessingPopup from "../../../../utils/popup/ProcessingPopup";

import GeneralSettingsForm from "./GeneralSettingsForm";
import ScreeningProtocolForm from "./ScreeningProtocolForm";
import CriteriaReasonsForm from "./CriteriaReasonsForm";
import ScreeningFrameworkSelectionForm from "./ScreeningFrameworkSelectionForm";
import ScreeningTaggingForm from "./ScreeningTaggingForm";


const ScreeningSpaceFormNavigation = ({
    editingProject,
    onBackToProjects,
    onNavigateToDiscovery,
    backLabel,
    onOpenGenbi,
}) => {
    const navigate = useNavigate();
    const API_BASE_URL = "/pceapi/slr/form";
    const headers = useMemo(() => ({ Authorization: GetCookie("access_token") }), []);

    const step1Ref = useRef(null);
    const step3Ref = useRef(null);
    const step4Ref = useRef(null);
    const step5Ref = useRef(null);

    const [step3Valid, setStep3Valid] = useState(false);
    const [step5Valid, setStep5Valid] = useState(false);

    const [activeStep, setActiveStep] = useState(1);
    const [isAutoSaving, setIsAutoSaving] = useState(false);
    const [showUnsavedBackModal, setShowUnsavedBackModal] = useState(false);
    const [completedSteps, setCompletedSteps] = useState(new Set());

    const [projectMeta, setProjectMeta] = useState(null);
    const projectId =
        projectMeta?.project_id ||
        projectMeta?.projectId ||
        projectMeta?.fabric_id ||
        editingProject?.id ||
        null;

    const [stagesData, setStagesData] = useState(null);
    const [configId, setConfigId] = useState(null);

    const [teamId, setTeamId] = useState(null);
    const [abstractsCount, setAbstractsCount] = useState(0);
    const [fullTextCount, setFullTextCount] = useState(0);
    const [availableModels, setAvailableModels] = useState([]);

    const [protocolMarkdown, setProtocolMarkdown] = useState("");

    const [generalSavedSnapshot, setGeneralSavedSnapshot] = useState(null);
    const [hasUnsavedGeneralChanges, setHasUnsavedGeneralChanges] = useState(false);
    const [latestGeneralSnapshot, setLatestGeneralSnapshot] = useState(null);


    const [protocolSavedSnapshot, setProtocolSavedSnapshot] = useState(null);
    const [hasUnsavedProtocolChanges, setHasUnsavedProtocolChanges] = useState(false);

    const [criteriaSavedSnapshot, setCriteriaSavedSnapshot] = useState(null);
    const criteriaSavedSnapshotRef = useRef(null);
    const [hasUnsavedCriteriaChanges, setHasUnsavedCriteriaChanges] = useState(false);


    const [frameworkSavedSnapshot, setFrameworkSavedSnapshot] = useState(null);
    const [hasUnsavedFrameworkChanges, setHasUnsavedFrameworkChanges] = useState(false);

    const [taggingSavedSnapshot, setTaggingSavedSnapshot] = useState(null);
    const [hasUnsavedTaggingChanges, setHasUnsavedTaggingChanges] = useState(false);

    // Promise for the background LLM criteria-generation call fired after
    // protocol save. CriteriaReasonsForm awaits it on first open.
    // Fired only while the project has no criteria (first time).
    const [criteriaGenPromise, setCriteriaGenPromise] = useState(null);

    // Bumped when the protocol is saved while criteria already exist: criteria
    // are left as they are, and Step 3 tells the user to click
    // "Update from Protocol" to apply the protocol to them.
    const [protocolSavedTick, setProtocolSavedTick] = useState(0);


    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
    const [popupErrorMessage, setPopupErrorMessage] = useState("");
    const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
    const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

    const cardRef = useRef(null);

    useLayoutEffect(() => {
        const el = cardRef.current;
        if (!el) return;
        const FOOTER_CLEARANCE = 70;
        const measure = () => {
            const top = el.getBoundingClientRect().top;
            const avail = Math.max(320, window.innerHeight - top - FOOTER_CLEARANCE);
            el.style.setProperty("--slr-wizard-avail", `${avail}px`);
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [activeStep]);

    useEffect(() => {
        let isMounted = true;

        const loadProject = async () => {
            if (editingProject?.id && isMounted) {
                await fetchProjectDetails(editingProject.id);
            }
        };

        loadProject();

        return () => {
            isMounted = false;
        };
    }, [editingProject?.id]);

    // ✅ compute dirty flag whenever protocol changes
    useEffect(() => {
        if (activeStep !== 2) return;

        if (protocolSavedSnapshot === null) {
            setProtocolSavedSnapshot(protocolMarkdown || "");
            return;
        }

        // Simple string comparison - sanitization happens only in ScreeningProtocolForm's displayContent
        const current = (protocolMarkdown || "").trim();
        const saved = (protocolSavedSnapshot || "").trim();
        setHasUnsavedProtocolChanges(current !== saved);
    }, [activeStep, protocolMarkdown, protocolSavedSnapshot]);


    const normalizeCriteriaList = (list = []) => {
        const copy = Array.isArray(list) ? [...list] : [];
        // Filter out blank-reason rows (default placeholder rows) before snapshotting
        // so that the 3 empty default rows don't count as "saved" content
        const meaningful = copy.filter((c) => (c.reason || "").trim().length > 0);
        meaningful.sort((a, b) => (a?.order_id ?? 0) - (b?.order_id ?? 0));
        return meaningful.map((c, idx) => ({
            order_id: idx + 1,
            criteria: c.criteria, // EXCLUSION / INCLUSION
            reason: (c.reason || "").trim(),
            abstract_screening: !!c.abstract_screening,
            full_text_screening: !!c.full_text_screening,
        }));
    };

    const handleCriteriaDataChange = useCallback((criteriaData) => {
        const normalized = normalizeCriteriaList(criteriaData);
        const serialized = JSON.stringify(normalized);
        // If baseline not set yet (e.g., new project with default rows), initialize it
        if (criteriaSavedSnapshotRef.current === null) {
            criteriaSavedSnapshotRef.current = serialized;
            setCriteriaSavedSnapshot(serialized);
            setHasUnsavedCriteriaChanges(false);
            return;
        }
        setHasUnsavedCriteriaChanges(serialized !== criteriaSavedSnapshotRef.current);
    }, []);


    const normalizeFrameworkData = (data) => {
        if (!data) {
            return {
                framework: null,
                phase1_ai_configs: [],
                phase2_ai_configs: [],
                phase1_human_allocations: [],
                phase2_human_allocations: [],
            };
        }

        const sortAlloc = (arr = []) =>
            (Array.isArray(arr) ? [...arr] : [])
                .map((a) => ({
                    reviewer_id: a.reviewer_id,
                    reviewer_set: a.reviewer_set,
                    allocation_range: {
                        start: Number(a?.allocation_range?.start),
                        end: Number(a?.allocation_range?.end),
                    },
                }))
                .filter((a) => Number.isFinite(a.allocation_range.start) && Number.isFinite(a.allocation_range.end))
                .sort((x, y) => (x.reviewer_set || "").localeCompare(y.reviewer_set || "") || (x.reviewer_id ?? 0) - (y.reviewer_id ?? 0));

        const sortAi = (arr = []) =>
            (Array.isArray(arr) ? [...arr] : [])
                .map((c) => ({
                    model_identifier: c.model_identifier,
                    config_json: {
                        temperature: Number.isFinite(parseFloat(c?.config_json?.temperature))
                            ? parseFloat(c.config_json.temperature)
                            : 0.2,
                    },
                }))
                .sort((x, y) => (x.model_identifier || "").localeCompare(y.model_identifier || ""));

        return {
            framework: data.framework ?? null,
            phase1_ai_configs: sortAi(data.phase1_ai_configs),
            phase2_ai_configs: sortAi(data.phase2_ai_configs),
            phase1_human_allocations: sortAlloc(data.phase1_human_allocations),
            phase2_human_allocations: sortAlloc(data.phase2_human_allocations),
        };
    };

    const handleFrameworkDataChange = (frameworkData) => {
        const normalized = normalizeFrameworkData(frameworkData);
        const serialized = JSON.stringify(normalized);

        if (frameworkSavedSnapshot === null) {
            setFrameworkSavedSnapshot(serialized);
            setHasUnsavedFrameworkChanges(false);
            return;
        }
        setHasUnsavedFrameworkChanges(serialized !== frameworkSavedSnapshot);
    };

    useEffect(() => {
        if (activeStep !== 4) return;
        if (frameworkSavedSnapshot !== null) return;

        const current = step4Ref.current?.getFrameworkData?.();
        const normalized = normalizeFrameworkData(current);
        setFrameworkSavedSnapshot(JSON.stringify(normalized));
        setHasUnsavedFrameworkChanges(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep, frameworkSavedSnapshot]);


    useEffect(() => {
        if ((activeStep === 2 || activeStep === 4) && projectId) {
            refreshProjectCounts(projectId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep, projectId]);


    const normalizeTagsData = (tags = []) => {
        const list = Array.isArray(tags) ? tags : [];
        return list.map((t) => ({
            picos_category: (t?.picos_category || "OTHER"),
            subcategory: (t?.subcategory || "Unclassified"),
            tag: (t?.tag || "").trim(),
            source: (t?.source || "MANUAL"),
            sample_frequency:
                t?.sample_frequency === "" || t?.sample_frequency === undefined
                    ? null
                    : t?.sample_frequency,
        }));
    };

    const handleTaggingDataChange = (tagsData) => {
        const normalized = normalizeTagsData(tagsData);
        const serialized = JSON.stringify(normalized);

        if (taggingSavedSnapshot === null) {
            setTaggingSavedSnapshot(serialized);
            setHasUnsavedTaggingChanges(false);
            return;
        }
        setHasUnsavedTaggingChanges(serialized !== taggingSavedSnapshot);
    };

    const handleGeneralDataChange = (generalPayload) => {
        const serialized = JSON.stringify(generalPayload || {});
        setLatestGeneralSnapshot(serialized);

        if (generalSavedSnapshot === null) {
            setGeneralSavedSnapshot(serialized);
            setHasUnsavedGeneralChanges(false);
            return;
        }
        setHasUnsavedGeneralChanges(serialized !== generalSavedSnapshot);
    };

    useEffect(() => {
        if (activeStep !== 5) return;
        if (taggingSavedSnapshot !== null) return;

        const current = step5Ref.current?.getTagsData?.();
        const normalized = normalizeTagsData(current);
        setTaggingSavedSnapshot(JSON.stringify(normalized));
        setHasUnsavedTaggingChanges(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep, taggingSavedSnapshot]);




    // True when the project already has criteria — saved ones, or rows filled
    // in Step 3 (the form stays mounted, so its rows include saved criteria).
    const hasAnyCriteria = () => {
        const current = step3Ref.current?.getCriteriaData?.() || [];
        if (current.some((c) => (c.reason || "").trim())) return true;
        const saved = criteriaSavedSnapshotRef.current;
        if (saved && saved !== "[]") return true;
        return (stagesData?.criteria?.data?.criteria_list?.length ?? 0) > 0;
    };

    // Fire a background LLM generate-criteria call — FIRST TIME ONLY.
    // Called after protocol save and after protocol load/auto-seed, but only
    // while the project has no criteria. After that criteria change only when
    // the user clicks "Update from Protocol" in Step 3.
    // CriteriaReasonsForm awaits the returned Promise when Step 3 opens.
    const fireCriteriaGeneration = useCallback((fabricId) => {
        if (!fabricId) return;
        const promise = axios.post(
            `${API_BASE_URL}/generate-criteria`,
            {
                fabric_id: fabricId,
                use_protocol: true,
                prompt: "Generate criteria from the screening protocol.",
                sample_size: 20,
            },
            { headers },
        );
        setCriteriaGenPromise(promise);
    }, [headers]);

    const fetchProjectDetails = async (slrFabricId) => {
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Loading project details");

        try {
            const response = await axios.post(
                `${API_BASE_URL}/get-project-details`,
                { slr_fabric_id: slrFabricId },
                { headers },
            );

            const data = response.data;

            if (data.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (data.status === "failed") {
                setPopupErrorMessage(data.message || "Failed to load project details");
                setIsErrorPopupVisible(true);
                return;
            }

            const stages = data.data?.stages;
            setStagesData(stages);
            setConfigId(data.data?.config_id);
            setAvailableModels(data.data?.available_models || []);

            if (stages?.general_settings?.data) {
                const gs = stages.general_settings.data;
                setTeamId(gs.team_id || null);
                setAbstractsCount(gs.abstracts_count || 0);
                setFullTextCount(gs.full_text_docs_count || 0);

                const generalBaseline = {
                    project_name: (gs?.name || "").trim(),
                    project_description: (gs?.description || "").trim(),
                    access_level: gs?.fabric_access_level || "PRIVATE",
                    team_id: gs?.team_id ?? null,
                    abstract_csv: null,
                    full_text_files: [],
                };
                const serialized = JSON.stringify(generalBaseline);
                setGeneralSavedSnapshot(serialized);
                setLatestGeneralSnapshot(serialized);
                setHasUnsavedGeneralChanges(false);
            }

            const completed = new Set();
            if (stages?.general_settings?.status === "COMPLETED") completed.add(1);
            if (stages?.screening_protocol?.status === "COMPLETED") completed.add(2);
            if (stages?.criteria?.status === "COMPLETED") completed.add(3);
            if (stages?.framework_selection?.status === "COMPLETED") completed.add(4);
            if (stages?.tagging?.status === "COMPLETED") completed.add(5);
            setCompletedSteps(completed);


            // ✅ Initialize criteria snapshot if available (step 3)
            if (stages?.criteria?.data?.criteria_list) {
                const normalized = normalizeCriteriaList(stages.criteria.data.criteria_list);
                const serialized = JSON.stringify(normalized);
                criteriaSavedSnapshotRef.current = serialized;
                setCriteriaSavedSnapshot(serialized);
                setHasUnsavedCriteriaChanges(false);
                // Criteria already saved — cancel any pending auto-generation
                if (stages.criteria.data.criteria_list.length > 0) {
                    setCriteriaGenPromise(null);
                }
            }

            // ✅ Initialize framework snapshot if available (step 4)
            if (stages?.framework_selection?.data) {
                const normalized = normalizeFrameworkData(stages.framework_selection.data);
                setFrameworkSavedSnapshot(JSON.stringify(normalized));
                setHasUnsavedFrameworkChanges(false);
            }

            // ✅ Initialize tagging snapshot if available (step 5)
            if (stages?.tagging?.data?.tags) {
                const normalized = normalizeTagsData(stages.tagging.data.tags);
                setTaggingSavedSnapshot(JSON.stringify(normalized));
                setHasUnsavedTaggingChanges(false);
            }




            if (stages?.screening_protocol?.data?.protocol_content) {
                const loaded = stages.screening_protocol.data.protocol_content;

                setProtocolMarkdown(loaded);
                setProtocolSavedSnapshot(null);
                setHasUnsavedProtocolChanges(false);

                // Protocol exists but no criteria yet (auto-seed from discovery, or
                // user saved protocol but hasn't opened Criteria tab yet).
                // Fire LLM criteria generation now so it's ready when Step 3 opens.
                const criteriaEmpty = !stages?.criteria?.data?.criteria_list?.length;
                if (criteriaEmpty) {
                    fireCriteriaGeneration(slrFabricId);
                }
            }
        } catch (err) {
            console.error("Error fetching project details:", err);
            setPopupErrorMessage(err.message || "Failed to load project details");
            setIsErrorPopupVisible(true);
        } finally {
            setIsProcessingPopupVisible(false);
        }
    };


    const refreshProjectCounts = async (slrFabricId) => {
        if (!slrFabricId) return;

        try {
            const response = await axios.post(
                `${API_BASE_URL}/papers-count`,
                { slr_fabric_id: slrFabricId },
                { headers },
            );

            const data = response.data;

            if (data.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (data.status === "failed") {
                console.error("Failed to refresh project counts:", data.message);
                return;
            }

            const newAbstractsCount = data?.data?.abstracts_count ?? 0;
            const newFullTextCount = data?.data?.full_text_docs_count ?? 0;

            setAbstractsCount(newAbstractsCount);
            setFullTextCount(newFullTextCount);

            // ✅ Keep stagesData consistent so any child using stageData stays correct
            setStagesData((prev) => {
                if (!prev?.general_settings?.data) return prev;
                return {
                    ...prev,
                    general_settings: {
                        ...prev.general_settings,
                        data: {
                            ...prev.general_settings.data,
                            abstracts_count: newAbstractsCount,
                            full_text_docs_count: newFullTextCount,
                        },
                    },
                };
            });

        } catch (err) {
            console.error("Error refreshing project counts:", err);
        }
    };


    /* ===================== UPDATE PROJECT API ===================== */
    const updateProjectStage = async (stage, data, files = null, onFailed = null) => {
        if (!projectId) {
            setPopupErrorMessage("Please create a project first");
            setIsErrorPopupVisible(true);
            setActiveStep(1);
            return false;
        }

        setIsProcessingPopupVisible(true);
        setProcessingMessage(`Saving ${stage.replace(/_/g, " ")}`);

        try {
            const formData = new FormData();
            formData.append("slr_fabric_id", String(projectId));
            formData.append("stage", stage);
            formData.append("data", JSON.stringify(data));

            if (files && files.length > 0) {
                files.forEach((file) => {
                    formData.append("files", file, file.name);
                });
            }

            const response = await axios.post(`${API_BASE_URL}/update-project`, formData, {
                headers: { ...headers },
            });

            const result = response.data;

            if (result.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return false;
            }

            if (result.status === "failed") {
                setPopupErrorMessage(result.message || `Failed to update ${stage}`);
                setIsErrorPopupVisible(true);
                onFailed?.(result);
                return false;
            }

            setPopupSuccessMessage(
                result.message || `${stage.replace(/_/g, " ")} updated successfully`,
            );
            setIsSuccessPopupVisible(true);
            return true;
        } catch (err) {
            console.error(`Error updating ${stage}:`, err);
            setPopupErrorMessage(
                err?.response?.data?.message || err.message || `Failed to update ${stage}`,
            );
            setIsErrorPopupVisible(true);
            return false;
        } finally {
            setIsProcessingPopupVisible(false);
        }
    };

    const markStepCompleted = (stepId) => {
        setCompletedSteps((prev) => new Set([...prev, stepId]));
    };

    const canAccessStep = (stepId) => {
        if (stepId === 1) return true;
        return !!projectId;
    };

    useEffect(() => {
        if (activeStep !== 1 && !projectId) {
            setActiveStep(1);
        }
    }, [activeStep, projectId]);

    const isActive = (stepId) => activeStep === stepId;
    const isCompleted = (stepId) => completedSteps.has(stepId);

    const getStepState = (stepId) => {
        if (isActive(stepId)) return "active";
        if (isCompleted(stepId)) return "completed";
        return "pending";
    };


    const getStepPillClass = (stepId) => {
        const active = isActive(stepId);
        const completed = isCompleted(stepId);

        if (active && !completed) {
            return "ev-btn-light-fill ev-btn-reg ev-radius-xlarge slr-step-pill pending active-pending ps-2";
        }

        if (!active && !completed) {
            return "ev-btn-light-fill ev-btn-reg ev-radius-xlarge slr-step-pill pending ps-2";
        }

        if (active && completed) {
            return "ev-btn-reg ev-radius-xlarge slr-step-pill ps-2 active";
        }

        return "ev-btn-reg ev-radius-xlarge slr-step-pill ps-2 completed";
    };




    const isSaveDisabled = useMemo(() => {
        if (activeStep === 1) return false;
        if (!projectId) return true;

        if (activeStep === 2) return (protocolMarkdown || "").trim().length === 0;
        if (activeStep === 3) return !step3Valid;
        if (activeStep === 4) return false;
        if (activeStep === 5) return !step5Valid;

        return false;
    }, [activeStep, projectId, protocolMarkdown, step3Valid, step5Valid]);

        const stepHasUnsavedChanges = (step) => {
        if (step === 1) return !!projectId && hasUnsavedGeneralChanges;
        if (step === 2) return hasUnsavedProtocolChanges;
        if (step === 3) return hasUnsavedCriteriaChanges;
        if (step === 4) return hasUnsavedFrameworkChanges;
        if (step === 5) return hasUnsavedTaggingChanges;
        return false;
    };

    // Persist the current step before leaving it.
    const goToStep = async (stepId) => {
        if (stepId === activeStep) return;

        if (stepHasUnsavedChanges(activeStep)) {
            setIsAutoSaving(true);
            const saved = await handleSave();
            setIsAutoSaving(false);
            if (!saved) return;
        }

        setActiveStep(stepId);
    };

    const handleBackClick = () => {
        if (stepHasUnsavedChanges(activeStep)) {
            setShowUnsavedBackModal(true);
            return;
        }
        onBackToProjects();
    };

    const handleSaveAndGoBack = async () => {
        setIsAutoSaving(true);
        const saved = await handleSave();
        setIsAutoSaving(false);
        setShowUnsavedBackModal(false);
        if (saved) onBackToProjects();
    };

    const handleSave = async () => {
        if (!projectId && activeStep !== 1) {
            setPopupErrorMessage("Please create the project in General Settings first.");
            setIsErrorPopupVisible(true);
            setActiveStep(1);
            return false;
        }

        if (activeStep === 1) {
            return (await step1Ref.current?.submitGeneralSettings?.()) === true;
        }

        if (activeStep === 2) {
            // Save raw content - sanitization is only needed for display in MDXEditor
            const success = await updateProjectStage("screening_protocol", {
                protocol_content: protocolMarkdown,
            });
            if (success) {
                markStepCompleted(2);
                setProtocolSavedSnapshot(protocolMarkdown);
                setHasUnsavedProtocolChanges(false);
                if (hasAnyCriteria()) {
                    // Criteria exist — leave them; Step 3 shows "Update from Protocol" hint.
                    setProtocolSavedTick((t) => t + 1);
                } else {
                    // First time: create criteria from the protocol.
                    fireCriteriaGeneration(projectId);
                }
            }
            return success;
        }

        if (activeStep === 3) {
            const criteriaData = step3Ref.current?.getCriteriaData?.();
            if (!criteriaData) {
                setPopupErrorMessage("Unable to get criteria data");
                setIsErrorPopupVisible(true);
                return false;
            }
            const success = await updateProjectStage(
                "criteria",
                { criteria_list: criteriaData },
                null,
                // Backend refuses to delete criteria cited by screening decisions —
                // put those back in the form so the user can just save again.
                (res) => {
                    if (res?.criteria_in_use?.length) {
                        step3Ref.current?.restoreInUseCriteria?.(res.criteria_in_use);
                    }
                },
            );
            if (success) {
                markStepCompleted(3);
                const normalized = normalizeCriteriaList(criteriaData);
                const serialized = JSON.stringify(normalized);
                criteriaSavedSnapshotRef.current = serialized;
                setCriteriaSavedSnapshot(serialized);
                setHasUnsavedCriteriaChanges(false);
            }
            return success;
        }

        if (activeStep === 4) {
            if (step4Ref.current?.hasErrors?.()) {
                setPopupErrorMessage("Please fix allocation range errors before saving");
                setIsErrorPopupVisible(true);
                return false;
            }

            const frameworkData = step4Ref.current?.getFrameworkData?.();
            if (!frameworkData) {
                setPopupErrorMessage("Unable to get framework data");
                setIsErrorPopupVisible(true);
                return false;
            }

            const success = await updateProjectStage("framework", frameworkData);
            if (success) {
                markStepCompleted(4);
                const normalized = normalizeFrameworkData(frameworkData);
                setFrameworkSavedSnapshot(JSON.stringify(normalized));
                setHasUnsavedFrameworkChanges(false);
            }
            return success;
        }

        if (activeStep === 5) {
            const tagsData = step5Ref.current?.getTagsData?.();
            if (!tagsData) {
                setPopupErrorMessage("Unable to get tags data");
                setIsErrorPopupVisible(true);
                return false;
            }

            const success = await updateProjectStage("tagging", { tags: tagsData });
            if (success) {
                markStepCompleted(5);
                const normalized = normalizeTagsData(tagsData);
                setTaggingSavedSnapshot(JSON.stringify(normalized));
                setHasUnsavedTaggingChanges(false);
            }
            return success;
        }
        return true;
    };

    const handleStep1Success = (msg, responseData) => {
        const fabricId =
            responseData?.fabric_id ||
            responseData?.project_id ||
            responseData?.projectId ||
            responseData?.payload?.slr_fabric_id ||
            responseData?.payload?.fabric_id ||
            responseData?.payload?.project_id ||
            null;

        if (fabricId) {
            setProjectMeta({ ...responseData, project_id: fabricId });
            markStepCompleted(1);
            refreshProjectCounts(fabricId);
            // A brand-new project has no `editingProject.id` yet, so the
            // edit-mode effect above never runs `fetchProjectDetails` for it —
            // meaning `availableModels` (and configId/stagesData) would stay
            // empty for the rest of this session. Fetch it now, the same way
            // editing an existing project does, so the AI model dropdown in
            // Step 4 isn't stuck disabled the first time through.
            fetchProjectDetails(fabricId);
        }

        const refreshed = responseData?.refreshed_stage_data;
        if (refreshed) {
            const gs = refreshed?.stages?.general_settings?.data;
            if (gs) {
                setAbstractsCount(gs.abstracts_count || 0);
                setFullTextCount(gs.full_text_docs_count || 0);
            }

            const protocolContent = refreshed?.stages?.screening_protocol?.data?.protocol_content;
            if (protocolContent) {
                setProtocolMarkdown(protocolContent);
                setProtocolSavedSnapshot("");
                setHasUnsavedProtocolChanges(true);
            }
        }

        const resolvedTeamId =
            responseData?.team_id ??
            responseData?.payload?.team_id ??
            null;

        if (resolvedTeamId) {
            setTeamId(resolvedTeamId);
        }

        // Mark step-1 data as saved for unsaved-changes banner
        setGeneralSavedSnapshot(null);
        setHasUnsavedGeneralChanges(false);

        setPopupSuccessMessage(msg || "Project created successfully");
        setIsSuccessPopupVisible(true);
    };

    const renderStepButton = (stepId, label) => {
        const locked = !canAccessStep(stepId);

        return (
            <button
                type="button"
                disabled={locked || isAutoSaving}
                className={`${getStepPillClass(stepId)} ${locked ? "is-locked" : ""}`}
                onClick={() => {
                    if (locked) {
                        setPopupErrorMessage("Please create the project in General Settings first.");
                        setIsErrorPopupVisible(true);
                        return;
                    }
                    goToStep(stepId);
                }}
                title={locked ? "Create project first" : ""}
            >
                <span
                    className={`slr-step-circle ${isCompleted(stepId) ? "completed" : (isActive(stepId) ? "active-pending" : "pending")
                        }`}
                >
                    {isCompleted(stepId) ? <i className="bi bi-check"></i> : null}
                </span>



                <span className="slr-step-label">{label}</span>

                {locked && <i className="bi bi-lock-fill ms-auto"></i>}

            </button>

        );
    };

    return (
        <div className="p-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="ev-med-p-dark mb-0">
                    {projectId ? "SLR Project Update" : "SLR Project Creation"}
                </p>

                <div className="d-flex align-items-center gap-2">
                    {stepHasUnsavedChanges(activeStep) && (
                        <div className="ev-alert-card-warning px-2 py-1 d-flex align-items-center gap-2 ev-radius-large">
                            <i className="bi bi-exclamation-triangle-fill text-warning"></i>
                            <span className="ev-reg-p-dark mb-0">Unsaved changes</span>
                        </div>
                    )}

                    <button
                        type="button"
                        className="ev-btn-blue-fill-round ev-btn-reg"
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                        title={isSaveDisabled ? "Please fill required fields first" : ""}
                    >
                        <i className="bi bi-check2-circle me-1"></i>
                        {activeStep === 1 && !projectId ? "Create Project" : "Save"}
                    </button>

                    <button
                        type="button"
                        className="ev-btn-blue-outline-round ev-btn-reg"
                        onClick={handleBackClick}
                    >
                        {backLabel || "Back to Projects"}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="ev-card-4 py-3 slr-wizard-card vh-70">
                {/* Step Navigation */}
                <div className="slr-stepper w-100 mb-3 p-1">
                    {renderStepButton(1, "General Settings")}
                    {renderStepButton(2, "Screening Protocol")}
                    {renderStepButton(3, "Exclusion & Inclusion Reasons")}
                    {renderStepButton(4, "Screening Framework Selection")}
                    {renderStepButton(5, "Tagging")}
                </div>

                {/* Step Content (keep all mounted; just show/hide) */}
                <div className={activeStep === 1 ? "w-100 slr-wizard-step" : "d-none"}>
                    <GeneralSettingsForm
                        ref={step1Ref}
                        editingProject={editingProject}
                        stageData={stagesData?.general_settings?.data}
                        onDataChange={handleGeneralDataChange}
                        onBackToProjects={onBackToProjects}
                        onNavigateToDiscovery={onNavigateToDiscovery}
                        onSuccess={handleStep1Success}
                    />
                </div>

                <div className={activeStep === 2 ? "w-100 slr-wizard-step" : "d-none"}>
                    <ScreeningProtocolForm
                        projectId={projectId}
                        configId={configId}
                        protocolMarkdown={protocolMarkdown}
                        setProtocolMarkdown={setProtocolMarkdown}
                        abstractsCount={abstractsCount}
                        onOpenGenbi={onOpenGenbi}
                    />
                </div>

                <div className={activeStep === 3 ? "w-100 slr-wizard-step" : "d-none"}>
                    <CriteriaReasonsForm
                        ref={step3Ref}
                        projectId={projectId}
                        stageData={stagesData?.criteria?.data}
                        protocolMarkdown={protocolMarkdown}
                        onValidityChange={setStep3Valid}
                        onDataChange={handleCriteriaDataChange}
                        criteriaGenPromise={criteriaGenPromise}
                        protocolSavedTick={protocolSavedTick}
                    />
                </div>

                <div className={activeStep === 4 ? "w-100 slr-wizard-step" : "d-none"}>
                    <ScreeningFrameworkSelectionForm
                        ref={step4Ref}
                        projectId={projectId}
                        teamId={teamId}
                        abstractsCount={abstractsCount}
                        fullTextCount={fullTextCount}
                        stageData={stagesData?.framework_selection?.data}
                        availableModels={availableModels}
                        onDataChange={handleFrameworkDataChange}
                        onRefreshCounts={() => refreshProjectCounts(projectId)}
                    />
                </div>

                <div className={activeStep === 5 ? "w-100 slr-wizard-step" : "d-none"}>
                    <ScreeningTaggingForm
                        ref={step5Ref}
                        projectId={projectId}
                        stageData={stagesData?.tagging?.data}
                        onValidityChange={setStep5Valid}
                        onDataChange={handleTaggingDataChange}
                    />
                </div>

            </div>

            {/* Popups */}
            {isProcessingPopupVisible && <ProcessingPopup message={processingMessage} />}
            {isErrorPopupVisible && (
                <ErrorPopup onClose={() => setIsErrorPopupVisible(false)} message={popupErrorMessage} />
            )}
                        {isSuccessPopupVisible && (
                <SuccessPopup onClose={() => setIsSuccessPopupVisible(false)} message={popupSuccessMessage} />
            )}

            {showUnsavedBackModal && (
                <div className="popup">
                    <div className="popup-content ev-padding-large ev-radius-small">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <i className="bi bi-exclamation-triangle-fill text-warning"></i>
                            <p className="ev-med-p-dark ev-bold-med mb-0">Unsaved changes</p>
                        </div>

                        <p className="ev-reg-p-dark mb-3">
                            You have unsaved changes on this step. Leaving now will discard them.
                        </p>

                        <div className="d-flex justify-content-end gap-2">
                            <button
                                type="button"
                                className="ev-btn-light-fill ev-btn-reg"
                                onClick={() => setShowUnsavedBackModal(false)}
                                disabled={isAutoSaving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="ev-btn-blue-outline-round ev-btn-reg"
                                onClick={() => { setShowUnsavedBackModal(false); onBackToProjects(); }}
                                disabled={isAutoSaving}
                            >
                                Discard &amp; Go Back
                            </button>
                            <button
                                type="button"
                                className="ev-btn-blue-fill-round ev-btn-reg"
                                onClick={handleSaveAndGoBack}
                                disabled={isAutoSaving}
                            >
                                <i className="bi bi-check2-circle me-1"></i>
                                {isAutoSaving ? "Saving…" : "Save & Go Back"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScreeningSpaceFormNavigation;